import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/EngagementDraftPostHistory.graphql';
import { Query, QueryengagementDraftPostHistoryArgs } from '../../__generated__/globalTypes';

export const useEngagementDraftPostHistoryQuery = (
  options?: QueryHookOptions<Query, QueryengagementDraftPostHistoryArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryengagementDraftPostHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useEngagementDraftPostHistoryLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryengagementDraftPostHistoryArgs>
): QueryResult<Query, QueryengagementDraftPostHistoryArgs> & {
  getEngagementDraftPostHistory: LazyQueryExecFunction<Query, QueryengagementDraftPostHistoryArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getEngagementDraftPostHistory, result] = useLazyQuery<Query, QueryengagementDraftPostHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getEngagementDraftPostHistory, ...result };
};

export const useEngagementDraftPostHistoryPromiseQuery = () => {
  const client = useApolloClient();

  const getEngagementDraftPostHistory = useCallback(
    (options?: Omit<QueryOptions<QueryengagementDraftPostHistoryArgs, Query>, 'query'>) =>
      client.query<Query, QueryengagementDraftPostHistoryArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getEngagementDraftPostHistory };
};
// Suspense Query Hooks
export const useEngagementDraftPostHistorySuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryengagementDraftPostHistoryArgs>
) => {
  const result = useSuspenseQuery<Query, QueryengagementDraftPostHistoryArgs>(QUERY, {
    ...options
  });

  return result;
};
